import type { FristForhaandsvisningDTO } from "@/api/person/fristForhaandsvisning";

/**
 * Hvad forhåndsvisningsboksen skal sige.
 *
 * Reglen ligger her og ikke i komponenten, fordi »i bero« ikke betyder det samme på de to
 * linjer: perioden kan være udløbet, mens fristen står stille. Den forgrening bliver
 * forkert i JSX uden tests, og repoet har ingen komponenttest-opsætning.
 */

export type FristMaerkat = "loebende" | "iBero" | "afventer";

export interface FristForhaandsvisningTekst {
  maerkat: FristMaerkat;
  maerkatTekst: string;
  /** Forklarende sætning, eller null når datoerne taler for sig selv. */
  forklaring: string | null;
  periode: string;
  frist: string;
}

const IKKE_PAABEGYNDT = "Ikke påbegyndt";

/**
 * Er bero-datoen samme dag som eller før afgørelsesdatoen? Så var advokaten allerede ude,
 * da kendelsen blev afsagt, og perioden er aldrig begyndt.
 *
 * Sammenlignes som datoer og ikke som strenge: et klokkeslet i den ene ende ville ellers
 * ændre resultatet.
 */
const erUdeAlleredeVedAfgoerelsen = (
  iBeroSiden: string,
  periodeFra: string,
): boolean => new Date(iBeroSiden).getTime() <= new Date(periodeFra).getTime();
const IKKE_FASTLAGT = "ikke fastlagt";

export const beregnFristForhaandsvisningTekst = (
  data: FristForhaandsvisningDTO,
  formatDate: (date: string | null | undefined) => string,
): FristForhaandsvisningTekst => {
  const fra = formatDate(data.periodeFra);

  // Fristen er beregnet: begge datoer ligger fast, og der er intet at forklare.
  if (data.frist) {
    return {
      maerkat: "loebende",
      maerkatTekst: "Løbende",
      forklaring: null,
      periode: `${fra} – ${formatDate(data.periodeTil)}`,
      frist: formatDate(data.frist),
    };
  }

  // Perioden er udløbet, men fristen står stille. Så er det fristen, bero-datoen hører til.
  if (data.periodeTil && data.iBeroSiden) {
    return {
      maerkat: "iBero",
      maerkatTekst: "I bero",
      forklaring:
        "Indfrielsesperioden er udløbet, men advokaten har ikke været udøvende " +
        `siden ${formatDate(data.iBeroSiden)}. De resterende måneder til ` +
        "dokumentation løber videre, når advokaten igen er udøvende.",
      periode: `${fra} – ${formatDate(data.periodeTil)}`,
      frist: `I bero siden ${formatDate(data.iBeroSiden)}`,
    };
  }

  // Advokaten er udøvende i dag, men beskikkelsen ophører, før perioden er gået. Forløbet
  // afventer frem for at have stået stille siden en bestemt dato.
  if (!data.iBeroSiden) {
    return {
      maerkat: "afventer",
      maerkatTekst: "Afventer",
      forklaring:
        "Advokaten ophører med at være udøvende, før indfrielsesperioden er gået, " +
        "så fristen kan ikke beregnes endnu.",
      periode: `${fra} – ${IKKE_FASTLAGT}`,
      frist: "—",
    };
  }

  // Perioden er slet ikke begyndt: advokaten var allerede ude, da kendelsen blev afsagt.
  // Bero-datoen kan ligge FØR afgørelsesdatoen — fx frakendt 17.08.2016, kendelse afsagt
  // 02.05.2017 — og så må linjen ikke sige "02.05.2017 – i bero", som om de 12 måneder var
  // begyndt at tælle. De tæller først, når advokaten igen er udøvende.
  if (erUdeAlleredeVedAfgoerelsen(data.iBeroSiden, data.periodeFra)) {
    return {
      maerkat: "iBero",
      maerkatTekst: "I bero",
      forklaring:
        `Advokaten har ikke været udøvende siden ${formatDate(data.iBeroSiden)}. ` +
        "Indfrielsesperioden begynder først at løbe, når advokaten igen er " +
        "udøvende, og der er derfor endnu ingen frist.",
      periode: IKKE_PAABEGYNDT,
      frist: "—",
    };
  }

  // Perioden var begyndt, og advokaten deponerede undervejs.
  return {
    maerkat: "iBero",
    maerkatTekst: "I bero",
    forklaring:
      `Advokaten har ikke været udøvende siden ${formatDate(data.iBeroSiden)}. ` +
      "Indfrielsesperioden står stille og løber videre, når advokaten igen er " +
      "udøvende.",
    periode: `${fra} – i bero`,
    frist: "—",
  };
};
